import { useMemo, useState } from "react";
import { Button } from "@cntrl/ui";
import { useBandContext, useCapability } from "../../app/bandContext.js";
import { errorMessage } from "../../app/hooks/apiError.js";
import { useSharedShows, useRespondShare, type SharedShowDetail } from "../../app/hooks/useShowShares.js";
import { dowOf, dayOf, sharedShowTab } from "./lib/bookingRows.js";
import { AcMark } from "./AcMark.js";
import styles from "./PendingInvitesGroup.module.scss";

/** "8:00 PM" from a `HH:mm[:ss]` TimeOnly, or null. Venue-local by construction (the vendor field is a wall
 *  clock, not an instant), so it is NOT device-localized. */
function formatTime(t: string | null | undefined): string | null {
  const m = /^(\d{2}):(\d{2})/.exec(t ?? "");
  if (!m) return null;
  const h = Number(m[1]);
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m[2]} ${period}`;
}

/**
 * The GUEST's pending show invites — a group pinned at the TOP of the Booking upcoming tabs (Ryan, ART-165:
 * a share invite lands here, "sitting on the confirmed section to accept", not on the schedule page). Each
 * card carries the date, the bill, the venue, the guest's SET time + length (from the host's run of show for
 * the guest's slot), and Accept / Deny — never a View button or a CONFIRMED badge.
 *
 * Reads the guest's shared shows (which now include `invited`, ART-165) and shows only the pending ones,
 * BUCKETED by the host show's status like the accepted rows: an invite to a date still on HOLD at its source
 * sits on the Holds tab, one to a confirmed date on Confirmed (`tab`). Accepted shares fold into the same tab
 * as ordinary rows. Self-hides when there is nothing to answer on this tab, so a band with no invites sees an
 * unchanged tab.
 */
export function PendingInvitesGroup({ tab }: { tab: "confirmed" | "holds" }) {
  const list = useSharedShows();
  const { bandId } = useBandContext();
  // Accepting/declining a shared show is schedule:write — Artist/AV, Tour Manager, and the Booking/Management
  // seats reaching the band (the same set the respond gate enforces). A seat that only READS the schedule (a
  // limited band member, a label seat) still SEES the invite — withhold the action, never the fact — but gets
  // a note in place of the buttons rather than an Accept that the server would 42501.
  const canRespond = useCapability("schedule:write");
  const invites = useMemo(
    () => (list.data ?? []).filter((s) => s.status === "invited" && sharedShowTab(s) === tab),
    [list.data, tab],
  );

  if (invites.length === 0) return null;

  return (
    <section className={styles.group} aria-labelledby="pending-invites-heading">
      <h3 id="pending-invites-heading" className={styles.title}>
        {canRespond ? "Invites — needs your response" : "Invites"}
      </h3>
      <ul className={styles.rows}>
        {invites.map((inv) => (
          <InviteCard key={inv.shareId} invite={inv} bandId={bandId} canRespond={canRespond} />
        ))}
      </ul>
    </section>
  );
}

function InviteCard({ invite, bandId, canRespond }: { invite: SharedShowDetail; bandId: string; canRespond: boolean }) {
  const respond = useRespondShare(invite.showId ?? "");
  const [error, setError] = useState<string | null>(null);

  // The guest's own slot is the lineup act the host attached to this band; its set time + length are the
  // run-of-show 'stage' row carrying that slot. Absent until the host has authored the run of show — "TBD"
  // then, never a fabricated time.
  const mySlotId = invite.lineup?.find((l) => l.friendBandId === bandId)?.slotId;
  const set = invite.runOfShow?.find((r) => r.section === "stage" && !!mySlotId && r.slotId === mySlotId);
  const setTime = formatTime(set?.startsAt);
  const setLen = set?.durationMin ?? null;
  const setLine = setTime ? `Set ${setTime}${setLen ? ` · ${setLen} min` : ""}` : "Set time TBD";

  // The bill as named acts, each lighting the AC mark when it is a band on Artist CNTRL (`friendBandId`) —
  // parity with the row and the modals. Falls back to the venue when a share carries no named lineup.
  const acts = (invite.lineup ?? []).filter((l) => (l.artistName ?? "").trim() !== "");
  const place = [invite.city, invite.state].filter((p) => (p ?? "").trim() !== "").join(", ");
  const venueLine = [invite.venueName, place].filter((p) => (p ?? "").trim() !== "").join(" · ");

  function answer(accept: boolean) {
    setError(null);
    respond.mutate(
      { shareId: invite.shareId as string, accept },
      { onError: (e) => setError(errorMessage(e, "Couldn't respond to the invite.")) },
    );
  }

  return (
    <li className={styles.card}>
      <span className={styles.dateBlock} aria-hidden="true">
        <span className={styles.dow}>{dowOf(invite.date ?? "")}</span>
        <span className={styles.day}>{dayOf(invite.date ?? "")}</span>
      </span>
      <div className={styles.body}>
        <p className={styles.bill}>
          {acts.length > 0
            ? acts.map((l, i) => (
                <span key={l.slotId ?? i}>
                  {i > 0 && " · "}
                  {l.artistName}
                  {l.friendBandId && <AcMark title={`${l.artistName ?? "This act"} is on Artist CNTRL`} />}
                </span>
              ))
            : invite.venueName || "Show"}
        </p>
        {venueLine && <p className={styles.venue}>{venueLine}</p>}
        <p className={styles.set}>{setLine}</p>
        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
      </div>
      <div className={styles.actions}>
        {canRespond ? (
          <>
            <Button size="sm" loading={respond.isPending} onClick={() => answer(true)}>
              Accept
            </Button>
            <Button variant="ghost" size="sm" disabled={respond.isPending} onClick={() => answer(false)}>
              Deny
            </Button>
          </>
        ) : (
          <p className={styles.note}>A manager or full-access member can accept this.</p>
        )}
      </div>
    </li>
  );
}
